import { useMultiFieldSet } from "@/components/utils/useFieldSet";
import { NonNullish } from "@/flokit/utils/comparison/non-nullish";
import {
  CandidateJobAppDocumentUploadRequest,
  JobAppSectionResponseSectionType,
} from "modules/api-docs";
import {
  DocumentFields,
  selfApplyDocumentUploadYupSchema,
} from "modules/self-apply/validation/candidate-self-apply";
import { useMemo } from "react";
import {
  SelfApplyOnlyBaseJobAppSectionFieldSetArgs,
  SelfApplyOnlyNullishExistingJobApp,
  SelfApplyOnlyNullish,
  SelfApplyOnlyNullishJobSection,
} from "./common";

export type SelfApplyOnlyDocumentSection = SelfApplyOnlyNullishJobSection & {
  jobCandidateDocuments?: SelfApplyOnlyNullish<
    SelfApplyOnlyNullish<{
      orgDocId?: SelfApplyOnlyNullish<number>;
      isRequired?: SelfApplyOnlyNullish<boolean>;
    }>[]
  >;
};

export function extractDocumentsFromExistingJobApp(args: {
  jobSections: SelfApplyOnlyDocumentSection[];
  existingJobApp: SelfApplyOnlyNullishExistingJobApp;
  orgFriendlyName: string;
  jobExtension: string;
}) {
  const existingAnswers: DocumentFields[] = [];
  if (!args.jobSections) {
    return existingAnswers;
  }

  const jobDocuments = args.jobSections
    .find(
      (jobSection) =>
        jobSection?.jobSectionType ===
        JobAppSectionResponseSectionType.CANDIDATE_DOCUMENTS,
    )
    ?.jobCandidateDocuments?.filter(NonNullish);
  if (!jobDocuments) {
    return existingAnswers;
  }

  for (const document of jobDocuments) {
    const orgDocId = document.orgDocId;
    if (!orgDocId) {
      continue;
    }

    const existingDocumentSubmission =
      args.existingJobApp?.jobAppDocuments?.find(
        (doc) => doc?.orgDocId === orgDocId,
      );

    const existingAnswer: DocumentFields = {
      orgFriendlyName: args.orgFriendlyName,
      jobExtension: args.jobExtension,
      orgcustomdocumentsId: orgDocId,
      createdAt: existingDocumentSubmission?.createdAt ?? undefined,
      // Will be set during submission
      isFinalStep: false,
    };

    existingAnswers.push(existingAnswer);
  }

  return existingAnswers;
}

type UseDocumentFieldSetArgs = SelfApplyOnlyBaseJobAppSectionFieldSetArgs & {
  jobSections: SelfApplyOnlyDocumentSection[];
  orgFriendlyName: string;
  jobExtension: string;
};
export function useDocumentFieldSet({
  jobSections,
  existingJobApp,
  orgFriendlyName,
  jobExtension,
}: UseDocumentFieldSetArgs) {
  const initialValue = useMemo(() => {
    return extractDocumentsFromExistingJobApp({
      jobSections,
      existingJobApp,
      orgFriendlyName,
      jobExtension,
    });
  }, [orgFriendlyName, jobExtension, jobSections, existingJobApp]);

  return useMultiFieldSet<DocumentFields>(
    initialValue,
    [initialValue],
    selfApplyDocumentUploadYupSchema,
  );
}

export function documentFieldsToRequests(args: {
  fields: DocumentFields[];
  jobExtension: string;
  orgFriendlyName: string;
}): CandidateJobAppDocumentUploadRequest[] {
  const withoutEmptySubmissions = args.fields.filter(
    (document) => !!document.file,
  );

  return withoutEmptySubmissions.map((document, index) => {
    const request: CandidateJobAppDocumentUploadRequest = {
      jobExtension: args.jobExtension,
      orgFriendlyName: args.orgFriendlyName,
      jobAppId: document.jobAppId,
      orgcustomdocumentsId: document.orgcustomdocumentsId,
      document: document.file,
      // The backend requires the last document in the sequence should have isFinalStep: true
      isFinalStep: index === withoutEmptySubmissions.length - 1,
    };

    return request;
  });
}
